import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
} from '@mantine/core';
import { IconPackageImport, IconPlus } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useBranches } from '../branches/branchesApi';
import { CreatePurchaseOrderModal } from './CreatePurchaseOrderModal';
import { ReceivePurchaseOrderModal } from './ReceivePurchaseOrderModal';
import { usePurchaseOrders } from './purchasingApi';
import {
  PURCHASE_ORDER_STATUS,
  type PurchaseOrderDto,
  type PurchaseOrderStatus,
} from './types';

const STATUS_KEYS: Record<PurchaseOrderStatus, string> = {
  [PURCHASE_ORDER_STATUS.DRAFT]: 'purchasing.po.status.draft',
  [PURCHASE_ORDER_STATUS.SUBMITTED]: 'purchasing.po.status.submitted',
  [PURCHASE_ORDER_STATUS.PARTIALLY_RECEIVED]: 'purchasing.po.status.partially',
  [PURCHASE_ORDER_STATUS.RECEIVED]: 'purchasing.po.status.received',
  [PURCHASE_ORDER_STATUS.CANCELLED]: 'purchasing.po.status.cancelled',
};

const STATUS_COLOR: Record<PurchaseOrderStatus, string> = {
  [PURCHASE_ORDER_STATUS.DRAFT]: 'gray',
  [PURCHASE_ORDER_STATUS.SUBMITTED]: 'blue',
  [PURCHASE_ORDER_STATUS.PARTIALLY_RECEIVED]: 'orange',
  [PURCHASE_ORDER_STATUS.RECEIVED]: 'green',
  [PURCHASE_ORDER_STATUS.CANCELLED]: 'red',
};

export function PurchaseOrdersPage() {
  const { t, i18n } = useTranslation();
  const { data: branches } = useBranches();

  const [branchId, setBranchId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState<PurchaseOrderDto | null>(null);

  const { data, isLoading, error } = usePurchaseOrders(branchId, statusFilter);

  const branchOptions = useMemo(
    () => [
      { value: '', label: t('purchasing.po.allBranches') },
      ...(branches?.map((b) => ({ value: b.branchId, label: b.nameAr })) ?? []),
    ],
    [branches, t],
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('purchasing.po.allStatuses') },
      ...Object.entries(STATUS_KEYS).map(([v, k]) => ({ value: v, label: t(k) })),
    ],
    [t],
  );

  return (
    <Stack gap="md">
      <PageHeader
        title={t('nav.items.purchaseOrders')}
        actions={
          <Button
            color="wheels"
            leftSection={<IconPlus size={16} stroke={1.6} />}
            onClick={() => setCreateOpen(true)}
          >
            {t('purchasing.po.create')}
          </Button>
        }
      />

      <Paper p="md" withBorder>
        <Group gap="sm" wrap="wrap">
          <Select
            label={t('purchasing.po.branchFilter')}
            data={branchOptions}
            value={branchId ?? ''}
            onChange={(v) => setBranchId(v || null)}
            w={220}
          />
          <Select
            label={t('purchasing.po.statusFilter')}
            data={statusOptions}
            value={statusFilter ? String(statusFilter) : ''}
            onChange={(v) => setStatusFilter(v ? (Number(v) as PurchaseOrderStatus) : null)}
            w={220}
          />
        </Group>
      </Paper>

      {isLoading && <Group justify="center" py="xl"><Loader size="sm" /></Group>}
      {error && (
        <Alert color="red">
          {error instanceof Error ? error.message : t('purchasing.po.loadFailed')}
        </Alert>
      )}
      {data && data.length === 0 && !isLoading && (
        <Alert color="gray">{t('purchasing.po.empty')}</Alert>
      )}

      {data && data.length > 0 && (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('purchasing.po.number')}</Table.Th>
                <Table.Th>{t('purchasing.po.supplier')}</Table.Th>
                <Table.Th>{t('purchasing.po.orderedAt')}</Table.Th>
                <Table.Th>{t('purchasing.po.status.label')}</Table.Th>
                <Table.Th className="text-end">{t('purchasing.po.lineCount')}</Table.Th>
                <Table.Th className="text-end">{t('purchasing.po.total')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((po) => {
                const canReceive =
                  po.status === PURCHASE_ORDER_STATUS.SUBMITTED ||
                  po.status === PURCHASE_ORDER_STATUS.PARTIALLY_RECEIVED;
                return (
                  <Table.Tr key={po.purchaseOrderId}>
                    <Table.Td className="font-mono">{po.displayNumber}</Table.Td>
                    <Table.Td>{po.supplierNameAr}</Table.Td>
                    <Table.Td className="font-numeric">{formatDate(po.orderedAt, i18n.language)}</Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLOR[po.status]} variant="light">
                        {t(STATUS_KEYS[po.status])}
                      </Badge>
                    </Table.Td>
                    <Table.Td className="font-numeric text-end">{po.lines.length}</Table.Td>
                    <Table.Td className="font-numeric text-end">{formatMoney(po.totalCost)}</Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end">
                        {canReceive && (
                          <Button
                            size="xs"
                            variant="light"
                            color="wheels"
                            leftSection={<IconPackageImport size={14} stroke={1.6} />}
                            onClick={() => setReceiveTarget(po)}
                          >
                            {t('purchasing.po.receive')}
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <CreatePurchaseOrderModal opened={createOpen} onClose={() => setCreateOpen(false)} />
      <ReceivePurchaseOrderModal
        po={receiveTarget}
        opened={receiveTarget !== null}
        onClose={() => setReceiveTarget(null)}
      />
    </Stack>
  );
}

function formatDate(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-PS' : 'en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    numberingSystem: 'latn',
  }).format(new Date(iso));
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'ILS', minimumFractionDigits: 2,
  }).format(value);
}
